/**
 * Me → «Изменить карту». Loads the chart, sends only the changed fields.
 * The own chart («Я») is edited in Settings → Account, so it redirects there.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MeSubpageShell } from '../../../components/chrome/MeSubpageShell';
import { PersonForm, formFromChart, patchBody, type PersonFormValue } from '../../../components/me/PersonForm';
import { useChartDetail, useUpdateChart } from '../../../hooks/useCharts';
import { ApiError } from '../../../lib/api-error';
import styles from './ChartPage.module.css';

export function ChartEdit(): JSX.Element {
  const { t } = useTranslation(['me', 'common']);
  const navigate = useNavigate();
  const { id = '' } = useParams<{ id: string }>();
  const { chart, isLoading, isError, notFound, refetch } = useChartDetail(id);
  const update = useUpdateChart(id);
  const [errorCode, setErrorCode] = useState<string | null>(null);

  useEffect(() => {
    if (chart?.isSelf) void navigate('/settings/account', { replace: true });
  }, [chart?.isSelf, navigate]);

  const title = t('charts.form.editTitle');
  const toList = (): void => {
    void navigate('/me/charts', { replace: true });
  };

  if (notFound) {
    return (
      <MeSubpageShell title={title} onBack={toList}>
        <div className={styles.stack}>
          <p role="status">{t('charts.form.notFound')}</p>
          <button type="button" className={`${styles.retry} ${styles.back}`} onClick={toList}>
            {t('charts.form.backToList')}
          </button>
        </div>
      </MeSubpageShell>
    );
  }

  if (!chart || chart.isSelf) {
    return (
      <MeSubpageShell title={title}>
        {isError && !isLoading ? (
          <div className={styles.stack}>
            <p role="alert">{t('charts.form.loadError')}</p>
            <button type="button" className={`${styles.retry} ${styles.back}`} onClick={() => refetch()}>
              {t('charts.retry')}
            </button>
          </div>
        ) : !chart ? (
          <p className={styles.note} data-testid="chart-edit-loading">{t('common:states.loading')}</p>
        ) : null}
      </MeSubpageShell>
    );
  }

  const submit = async (v: PersonFormValue): Promise<void> => {
    setErrorCode(null);
    const body = patchBody(chart, v);
    if (Object.keys(body).length === 0) {
      navigate('/me/charts/' + id, { replace: true });
      return;
    }
    try {
      await update.mutateAsync(body);
      navigate('/me/charts/' + id, { replace: true });
    } catch (err) {
      setErrorCode(err instanceof ApiError ? err.code : 'unknown');
    }
  };

  return (
    <MeSubpageShell title={title} data-testid="me-chart-edit">
      <PersonForm
        key={chart.id}
        initial={formFromChart(chart)}
        submitLabel={t('charts.form.saveChanges')}
        busy={update.isPending}
        errorCode={errorCode}
        onSubmit={(v) => void submit(v)}
        onCancel={() => navigate(-1)}
      />
    </MeSubpageShell>
  );
}
